/**
 * Preiskonfiguration für SkyVision WALKON (eckig).
 *
 * Quelle: STAND November SkyVision Selbstbau mit Thiele Glas.xlsx
 * Modell WalkOn / WalkOn ecoline.
 *
 * VK = (Glas Thiele + UK + WalkOn-Mehrpreis + UK-Verschnitt 10 %) × 1,50
 *
 * Der 3-fach-Glasrest (73,58 €/m²) ist am gespeicherten Excel-Beispiel
 * WalkOn ecoline 770 × 1162 mm kalibriert (VK 2.897 €). 2-fach skaliert
 * über das Verhältnis TG-Therm plus 2-fach/3-fach (112/172).
 */

export const SKYVISION_WALKON_CONFIG = {
  baseDimensions: {
    width: 600,
    length: 600,
    height: 210,
  },
  glassOversizeMm: 260,
  frameOversizeMm: 280,
  innerLfm: 49.85,
  outerLfm: 88.55,
  walkOnExtraPerLfm: 22.5,
  setupCost: 100,
  energyRate: 0.1,
  wasteRate: 0.1,
  markup: 1.5,
  circularMarkup: 1.4,
  bendCost: 0,
  circularBendCost: 200,
  isoRemainderPerSqm: {
    double: 48,
    triple: 73.58,
    none: 0,
  },
  stepPricePerSqm: {
    27: 490.2,
    31: 560.5,
    35: 653.7,
    41: 901.8,
  } as Record<number, number>,
  glassTypes: {
    double: {
      name: '2-fach Isolierverglasung, begehbar',
      description: 'WalkOn mit TG-Step und 2-fach Isolierglas (Ug-Wert 1,1)',
      minHeight: 210,
    },
    triple: {
      name: '3-fach Isolierverglasung, begehbar',
      description: 'WalkOn ecoline mit TG-Step und 3-fach Isolierglas (Ug-Wert 0,7)',
      minHeight: 240,
    },
    none: {
      name: 'Begehbar ohne Isolierglas',
      description: 'Nur TG-Step, ohne Isolierglas (Excel WalkOn ohne Iso)',
      minHeight: 210,
    },
  },
};

export type WalkOnGlassType = 'double' | 'triple' | 'none';

export function normalizeWalkOnGlass(glassType?: string | null): WalkOnGlassType {
  if (glassType === 'triple' || glassType === 'none') return glassType;
  return 'double';
}

type WalkOnRuntimeConfig = {
  glassOversizeMm?: number;
  frameOversizeMm?: number;
  innerLfm?: number;
  outerLfm?: number;
  walkOnExtraPerLfm?: number;
  setupCost?: number;
  energyRate?: number;
  wasteRate?: number;
  markup?: number;
  bendCost?: number;
  isoRemainderPerSqm?: { double?: number; triple?: number; none?: number };
  stepPricePerSqm?: Record<number, number>;
  maxArea?: number;
};

let walkOnRuntime: WalkOnRuntimeConfig | null = null;

function asNumber(value: unknown, fallback: number): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function applyWalkOnRuntimeConfig(config: Record<string, unknown> | null | undefined) {
  if (!config) return;
  const iso = (config.isoRemainderPerSqm || {}) as Record<string, unknown>;
  const stepsIn = (config.stepPricePerSqm || {}) as Record<string, unknown>;
  const stepPricePerSqm: Record<number, number> = {};
  [27, 31, 35, 41].forEach((step) => {
    const raw = stepsIn[step] ?? stepsIn[String(step)];
    if (raw != null) stepPricePerSqm[step] = asNumber(raw, SKYVISION_WALKON_CONFIG.stepPricePerSqm[step]);
  });
  walkOnRuntime = {
    glassOversizeMm: asNumber(config.glassOversizeMm, SKYVISION_WALKON_CONFIG.glassOversizeMm),
    frameOversizeMm: asNumber(config.frameOversizeMm, SKYVISION_WALKON_CONFIG.frameOversizeMm),
    innerLfm: asNumber(config.innerLfm, SKYVISION_WALKON_CONFIG.innerLfm),
    outerLfm: asNumber(config.outerLfm, SKYVISION_WALKON_CONFIG.outerLfm),
    walkOnExtraPerLfm: asNumber(config.walkOnExtraPerLfm, SKYVISION_WALKON_CONFIG.walkOnExtraPerLfm),
    setupCost: asNumber(config.setupCost, SKYVISION_WALKON_CONFIG.setupCost),
    energyRate: asNumber(config.energyRate, SKYVISION_WALKON_CONFIG.energyRate),
    wasteRate: asNumber(config.wasteRate, SKYVISION_WALKON_CONFIG.wasteRate),
    markup: asNumber(config.markup, SKYVISION_WALKON_CONFIG.markup),
    bendCost: config.bendCost != null ? asNumber(config.bendCost, 0) : undefined,
    isoRemainderPerSqm: {
      double: asNumber(iso.double, SKYVISION_WALKON_CONFIG.isoRemainderPerSqm.double),
      triple: asNumber(iso.triple, SKYVISION_WALKON_CONFIG.isoRemainderPerSqm.triple),
      none: asNumber(iso.none, 0),
    },
    stepPricePerSqm,
    maxArea: config.maxArea != null ? asNumber(config.maxArea, WALKON_DIMENSIONS.MAX_AREA) : undefined,
  };
}

export function getWalkOnConfig() {
  const base = SKYVISION_WALKON_CONFIG;
  const runtime = walkOnRuntime;
  if (!runtime) return base;
  return {
    ...base,
    ...runtime,
    isoRemainderPerSqm: {
      ...base.isoRemainderPerSqm,
      ...runtime.isoRemainderPerSqm,
    },
    stepPricePerSqm: {
      ...base.stepPricePerSqm,
      ...runtime.stepPricePerSqm,
    },
    glassTypes: base.glassTypes,
    baseDimensions: base.baseDimensions,
  };
}

export const WALKON_DIMENSIONS = {
  MIN_WIDTH: 600,
  MAX_WIDTH: 1600,
  MIN_LENGTH: 600,
  MAX_LENGTH: 3000,
  MIN_HEIGHT_DOUBLE: 210,
  MIN_HEIGHT_TRIPLE: 240,
  MAX_HEIGHT: 750,
  MAX_AREA: 4,
  STEP_SIZE: 100,
};

type StatikRun = [number, number, number];

const WALKON_STATIK: { L: number; runs: StatikRun[] }[] = [
  { L: 3000, runs: [[100, 600, 27], [700, 1000, 31], [1100, 1200, 35], [1300, 1600, 41]] },
  { L: 2900, runs: [[100, 600, 27], [700, 1000, 31], [1100, 1200, 35], [1300, 1600, 41]] },
  { L: 2800, runs: [[100, 600, 27], [700, 1000, 31], [1100, 1200, 35], [1300, 1600, 41]] },
  { L: 2700, runs: [[100, 600, 27], [700, 1000, 31], [1100, 1200, 35], [1300, 1600, 41]] },
  { L: 2600, runs: [[100, 600, 27], [700, 1000, 31], [1100, 1300, 35], [1400, 1700, 41]] },
  { L: 2500, runs: [[100, 600, 27], [700, 1000, 31], [1100, 1300, 35], [1400, 1800, 41]] },
  { L: 2400, runs: [[100, 600, 27], [700, 1100, 31], [1200, 1300, 35], [1400, 1900, 41]] },
  { L: 2300, runs: [[100, 600, 27], [700, 1100, 31], [1200, 1300, 35], [1400, 1900, 41]] },
  { L: 2200, runs: [[100, 600, 27], [700, 1100, 31], [1200, 1400, 35], [1500, 2100, 41]] },
  { L: 2100, runs: [[100, 600, 27], [700, 1100, 31], [1200, 1400, 35], [1500, 2200, 41]] },
  { L: 2000, runs: [[100, 600, 27], [700, 1200, 31], [1300, 1600, 35], [1700, 2200, 41]] },
  { L: 1900, runs: [[100, 600, 27], [700, 1200, 31], [1300, 1600, 35], [1700, 2400, 41]] },
  { L: 1800, runs: [[100, 600, 27], [700, 1200, 31], [1300, 1800, 35], [1900, 2500, 41]] },
  { L: 1700, runs: [[100, 600, 27], [700, 1300, 31], [1400, 1800, 35], [1900, 2600, 41]] },
  { L: 1600, runs: [[100, 600, 27], [700, 1400, 31], [1500, 2000, 35], [2100, 3000, 41]] },
  { L: 1500, runs: [[100, 600, 27], [700, 1500, 31], [1600, 2000, 35], [2100, 3000, 41]] },
  { L: 1400, runs: [[100, 700, 27], [800, 1600, 31], [1700, 2200, 35], [2300, 3000, 41]] },
  { L: 1300, runs: [[100, 700, 27], [800, 1700, 31], [1800, 2600, 35], [2700, 3000, 41]] },
  { L: 1200, runs: [[100, 700, 27], [800, 2000, 31], [2100, 3000, 35]] },
  { L: 1100, runs: [[100, 800, 27], [900, 2400, 31], [2500, 3000, 35]] },
  { L: 1000, runs: [[100, 1000, 27], [1100, 3000, 31]] },
  { L: 900, runs: [[100, 1000, 27], [1100, 3000, 31]] },
  { L: 800, runs: [[100, 1100, 27], [1200, 3000, 31]] },
  { L: 700, runs: [[100, 1400, 27], [1500, 3000, 31]] },
  { L: 600, runs: [[100, 3000, 27]] },
  { L: 500, runs: [[100, 3000, 27]] },
  { L: 400, runs: [[100, 3000, 27]] },
  { L: 300, runs: [[100, 3000, 27]] },
  { L: 200, runs: [[100, 3000, 27]] },
  { L: 100, runs: [[100, 3000, 27]] },
];

const roundUp100 = (value: number): number => Math.ceil(value / 100) * 100;

export function lookupWalkOnStep(widthMm: number, lengthMm: number): 27 | 31 | 35 | 41 {
  const cfg = getWalkOnConfig();
  const shortSide = Math.min(widthMm, lengthMm);
  const longSide = Math.max(widthMm, lengthMm);
  const glassShort = roundUp100(shortSide + cfg.glassOversizeMm);
  const glassLong = roundUp100(longSide + cfg.glassOversizeMm);
  const rowKey = Math.max(100, Math.min(3000, glassShort));
  const colKey = Math.max(100, Math.min(3500, glassLong));
  const row = WALKON_STATIK.find((entry) => entry.L === rowKey);
  if (!row) return 41;
  const match = row.runs.find(([from, to]) => colKey >= from && colKey <= to);
  return (match?.[2] as 27 | 31 | 35 | 41) || 41;
}

export type WalkOnPriceDetails = {
  totalPrice: number;
  glassNet: number;
  ukNet: number;
  walkOnExtra: number;
  waste: number;
  bendCost: number;
  selfCost: number;
  step: 27 | 31 | 35 | 41;
  glassArea: number;
  lightArea: number;
};

export type WalkOnCalcOptions = {
  circular?: boolean;
  diameter?: number;
};

function isoRemainderFor(glassType: WalkOnGlassType, cfg: ReturnType<typeof getWalkOnConfig>): number {
  if (glassType === 'none') return cfg.isoRemainderPerSqm.none ?? 0;
  return glassType === 'triple' ? cfg.isoRemainderPerSqm.triple : cfg.isoRemainderPerSqm.double;
}

export function calculateWalkOnPrice(
  width: number,
  length: number,
  glassType: WalkOnGlassType | string = 'double',
  options?: WalkOnCalcOptions,
): WalkOnPriceDetails {
  const cfg = getWalkOnConfig();
  const glass = normalizeWalkOnGlass(glassType);
  const circular = Boolean(options?.circular);
  const diameter = Math.max(
    0,
    options?.diameter || (circular ? Math.min(width, length) : 0),
  );

  let glassArea: number;
  let lightArea: number;
  let outerFrame: number;
  let innerFrame: number;
  let step: 27 | 31 | 35 | 41;

  if (circular && diameter > 0) {
    const glassD = diameter + cfg.glassOversizeMm;
    glassArea = Math.PI * Math.pow(glassD / 2000, 2);
    lightArea = Math.PI * Math.pow(diameter / 2000, 2);
    outerFrame = (Math.PI * (diameter + cfg.frameOversizeMm)) / 1000;
    innerFrame = (Math.PI * diameter) / 1000;
    step = lookupWalkOnStep(diameter, diameter);
  } else {
    const shortSide = Math.min(width, length);
    const longSide = Math.max(width, length);
    const glassW = shortSide + cfg.glassOversizeMm;
    const glassL = longSide + cfg.glassOversizeMm;
    glassArea = (glassW * glassL) / 1_000_000;
    lightArea = (shortSide * longSide) / 1_000_000;
    outerFrame = (((shortSide + cfg.frameOversizeMm) + (longSide + cfg.frameOversizeMm)) * 2) / 1000;
    innerFrame = (shortSide * 2 + longSide * 2) / 1000;
    step = lookupWalkOnStep(width, length);
  }

  const glassSqm = cfg.stepPricePerSqm[step] + isoRemainderFor(glass, cfg);
  const glassRaw = glassSqm * glassArea;
  const glassNet = glassRaw + cfg.setupCost + glassRaw * cfg.energyRate;
  const ukNet = cfg.innerLfm * innerFrame + cfg.outerLfm * outerFrame;
  const walkOnExtra = cfg.walkOnExtraPerLfm * outerFrame;
  const waste = ukNet * cfg.wasteRate;
  const bendCost = circular
    ? (walkOnRuntime?.bendCost ?? SKYVISION_WALKON_CONFIG.circularBendCost)
    : (cfg.bendCost ?? 0);
  const markup = circular
    ? (walkOnRuntime ? cfg.markup : SKYVISION_WALKON_CONFIG.circularMarkup)
    : cfg.markup;
  const selfCost = glassNet + ukNet + walkOnExtra + waste + bendCost;
  const totalPrice = Math.round(selfCost * markup);

  return {
    totalPrice,
    glassNet,
    ukNet,
    walkOnExtra,
    waste,
    bendCost,
    selfCost,
    step,
    glassArea,
    lightArea,
  };
}

export const WALKON_GLASS_OPTIONS = {
  sun_protection_glass: {
    id: 'sun_protection_glass',
    accessory_code: 'sun_protection_glass',
    name: 'Sonnenschutzglas',
    description: 'Reduziert die Sonneneinstrahlung (Excel: 100 €/m² bei 2-fach, 145 €/m² bei 3-fach)',
    price_type: 'per_sqm' as const,
    base_price: 135,
  },
  antirutsch_print: {
    id: 'antirutsch_print',
    accessory_code: 'antirutsch_print',
    name: 'Antirutsch Siebdruck',
    description: 'Rutschhemmung K 204 / P 100 / P 107',
    price_type: 'per_sqm' as const,
    base_price: 173,
  },
  antirutsch_structure: {
    id: 'antirutsch_structure',
    accessory_code: 'antirutsch_structure',
    name: 'Antirutsch Struktur',
    description: 'Madras Linea / Pixel / Matrix Flooring',
    price_type: 'per_sqm' as const,
    base_price: 225,
  },
};

export const ANTIRUTSCH_ACCESSORY_CODES = [
  'antirutsch_print',
  'antirutsch_structure',
] as const;

export function validateWalkOnDimensions(
  width: number,
  length: number,
  height: number,
  glassType: WalkOnGlassType | string = 'double',
): string[] {
  const errors: string[] = [];
  const minHeight =
    glassType === 'triple'
      ? WALKON_DIMENSIONS.MIN_HEIGHT_TRIPLE
      : WALKON_DIMENSIONS.MIN_HEIGHT_DOUBLE;
  if (width < WALKON_DIMENSIONS.MIN_WIDTH || width > WALKON_DIMENSIONS.MAX_WIDTH) {
    errors.push(`Breite ${WALKON_DIMENSIONS.MIN_WIDTH}–${WALKON_DIMENSIONS.MAX_WIDTH} mm`);
  }
  if (length < WALKON_DIMENSIONS.MIN_LENGTH || length > WALKON_DIMENSIONS.MAX_LENGTH) {
    errors.push(`Länge ${WALKON_DIMENSIONS.MIN_LENGTH}–${WALKON_DIMENSIONS.MAX_LENGTH} mm`);
  }
  if (height < minHeight || height > WALKON_DIMENSIONS.MAX_HEIGHT) {
    errors.push(`Höhe ${minHeight}–${WALKON_DIMENSIONS.MAX_HEIGHT} mm`);
  }
  const area = (width * length) / 1_000_000;
  if (area > WALKON_DIMENSIONS.MAX_AREA) {
    errors.push(`Maximale Fläche: ${WALKON_DIMENSIONS.MAX_AREA} m²`);
  }
  return errors;
}

export const getWalkOnDimensions = () => {
  const maxArea = walkOnRuntime?.maxArea;
  if (maxArea == null) return WALKON_DIMENSIONS;
  return { ...WALKON_DIMENSIONS, MAX_AREA: maxArea };
};

export const getWalkOnMinDimensions = () => ({
  width: SKYVISION_WALKON_CONFIG.baseDimensions.width,
  length: SKYVISION_WALKON_CONFIG.baseDimensions.length,
  height: SKYVISION_WALKON_CONFIG.baseDimensions.height,
});

export const getProductName = () => 'SkyVision WALKON';
